import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import PropTypes from 'prop-types'
import AutoSizer from 'react-virtualized-auto-sizer'
import { isNumber } from 'lodash'
import { ListingCard, Footer, TutorialTooltip } from '@adc/ui-adc'
import classnames from 'classnames/bind'
import NearbyAssetTotals from '../assetTotals/nearbyTotals'
import AssetTotals from '../assetTotals'
import NearbyCheckboxRedesign from '../nearbyCheckbox'
import ListingCardWrapper from '../listingCardWrapper'
import { getMaxResultsAllowed } from '../../util/maxResultsAllowed'
import { partitionNearbyAssets } from '../../util/nearbyCheckbox'
import SeoContent from '../seoContent'
import {
  getTargetRow,
  shouldDisplayAdglareCard,
  getPropertyTotalAmount,
} from '../../util/asset'
import { isMobileBreakpoint } from '../../util/osDetector'
import {
  blueprintRemoveSelectedListingQueryParams,
  getTargetListingToScroll,
} from '../../util/searchUrl'
import BreadCrumbs from '../breadCrumbs'
import ListHeaders from '../listHeaders'
import {
  useHoverSubscription,
  useIsHovered,
  useSetHover,
} from '../searchPanel/hoverContext'
import { useSavedState } from '../searchPanel/savedStateContext'
import {
  trackViewItemList,
  SRP_ITEM_LIST_ID,
  SRP_ITEM_LIST_NAME,
} from '../../services/analytics'
import { EXPERIMENT_FLAG_KEY } from '../../constants/sorting'
import styles from './styles.scss'
import { DynamicSizeList, scrollToTop } from './dynamicSizeList'
import {
  CARD_HEIGHT,
  CARD_WIDTH,
  GAP,
  MOBILE_WIDTH_BREAKPOINT,
} from './constants'

const cx = classnames.bind(styles)

const LoadingState = () => {
  const cardListMapper = Array.from({ length: 12 }, () => null)
  return (
    <div className={cx('loading-property-card-container')}>
      {cardListMapper.map((_item, index) => (
        <ListingCard loading key={`loading-card-${index}`} />
      ))}
    </div>
  )
}

const BASE_ELM_ID = 'asset-list'
const VARIABLE_SIZE_LIST_REDRAW_DELAY = 10

const CardItemBase = cardItemProps => {
  const {
    listingToRender,
    cardProps,
    resultSection,
    verifiedImage,
    i,
    showAdContent = false,
  } = cardItemProps
  const focused = useIsHovered(listingToRender.listing_id)
  const isSaved = useSavedState(
    listingToRender?.primary_property?.property_id,
    Boolean(listingToRender?.primary_property?.is_currently_saved)
  )

  let uniqueIdentifier = `asset_${listingToRender?.listing_id}`
  if (showAdContent) {
    uniqueIdentifier = `asset_inject_placeholder`
  }

  const cardWrapper = (
    <ListingCardWrapper
      key={uniqueIdentifier}
      elmId={uniqueIdentifier}
      listing={listingToRender}
      isSaved={isSaved}
      position={i}
      showAdContent={showAdContent}
      {...cardProps}
      resultSection={resultSection}
      verifiedImage={verifiedImage}
      isFocused={focused}
    />
  )
  return i === 0 ? (
    <TutorialTooltip
      tutorial={3}
      skip={isMobileBreakpoint()}
      position='bottom center'
      mobilePosition='bottom center'
      elmId='asset_row_tutorial'
      key={uniqueIdentifier}
      header='Browse Property Details'
      content='Review info such as address, pricing and auction date.'
    >
      {cardWrapper}
    </TutorialTooltip>
  ) : (
    cardWrapper
  )
}

export const CardItem = React.memo(CardItemBase)

const calculateItemsPerRow = width =>
  Math.max(Math.floor((width - GAP) / (CARD_WIDTH + GAP)), 1)

const ListingListComponentBase = ({
  assets,
  filters,
  featureFlags,
  toggleSaveAsset,
  updateSorting,
  updateFiltersAction,
  updateOpenedAsset,
}) => {
  const isNewNavigation = featureFlags['fe.resi.enable-new-navigation']
  const setHover = useSetHover()
  const onMouseLeave = e => {
    e.stopPropagation()
    setHover(0, 'list')
  }

  const history = useHistory()

  const listRef = useRef(null)
  const prevFetchProgressRef = useRef(null)
  const listVersionRef = useRef(0)

  const [itemsPerRow, setItemsPerRow] = useState(0)
  const [forceRemountKey, setForceRemountKey] = useState(0)

  const { list: assetsList, fetchProgress, total } = assets

  useEffect(() => {
    if (prevFetchProgressRef.current?.loading && !fetchProgress.loading) {
      listVersionRef.current += 1
      setForceRemountKey(listVersionRef.current)
    }
    prevFetchProgressRef.current = fetchProgress
  }, [fetchProgress])

  useEffect(() => {
    if (listRef.current) {
      const timeoutId = setTimeout(() => {
        if (listRef.current) {
          listRef.current.resetAfterIndex(0)
        }
      }, VARIABLE_SIZE_LIST_REDRAW_DELAY)

      return () => clearTimeout(timeoutId)
    }
  }, [assetsList.length, itemsPerRow])

  const [exactMatchList, nearbyAssetsList] = partitionNearbyAssets(
    filters,
    assetsList
  )

  const verifiedImagesList = useRef({})
  const trackedListingIds = useRef(new Set())
  const prevFetchProgressForTrackingRef = useRef(fetchProgress)

  // Reset tracked listings synchronously when new search results arrive.
  if (
    prevFetchProgressForTrackingRef.current?.loading &&
    !fetchProgress.loading
  ) {
    trackedListingIds.current = new Set()
  }
  prevFetchProgressForTrackingRef.current = fetchProgress

  const onItemsRendered = useCallback(
    ({ visibleStartIndex, visibleStopIndex }) => {
      if (!itemsPerRow || assetsList.length === 0) return

      const headerRowOffset = 0
      const exactMatchRowCount = Math.ceil(exactMatchList.length / itemsPerRow)
      const newlyVisibleListings = []

      for (
        let rowIndex = visibleStartIndex;
        rowIndex <= visibleStopIndex;
        rowIndex++
      ) {
        const adjustedRowIndex = rowIndex - headerRowOffset
        if (adjustedRowIndex < 0) continue

        const listingsForRow = []

        if (adjustedRowIndex < exactMatchRowCount) {
          // This is an exact match row
          const fromIndex = adjustedRowIndex * itemsPerRow
          const toIndex = Math.min(
            fromIndex + itemsPerRow,
            exactMatchList.length
          )
          for (let i = fromIndex; i < toIndex; i++) {
            const listing = exactMatchList[i]
            if (listing?.listing_id && !listing.injectPlaceholder) {
              listingsForRow.push({ listing, index: i })
            }
          }
        } else {
          // Account for the nearby header row
          const nearbyRowOffset =
            exactMatchRowCount +
            (nearbyAssetsList.length > 0 && exactMatchList.length > 0 ? 1 : 0)

          if (adjustedRowIndex >= nearbyRowOffset) {
            const nearbyRowIndex = adjustedRowIndex - nearbyRowOffset
            const fromIndex = nearbyRowIndex * itemsPerRow
            const toIndex = Math.min(
              fromIndex + itemsPerRow,
              nearbyAssetsList.length
            )
            for (let i = fromIndex; i < toIndex; i++) {
              const listing = nearbyAssetsList[i]
              if (listing?.listing_id) {
                listingsForRow.push({
                  listing,
                  index: i + exactMatchList.length,
                })
              }
            }
          }
        }

        listingsForRow.forEach(({ listing, index }) => {
          if (!trackedListingIds.current.has(listing.listing_id)) {
            trackedListingIds.current.add(listing.listing_id)
            newlyVisibleListings.push({ listing, index })
          }
        })
      }

      if (newlyVisibleListings.length > 0) {
        trackViewItemList({
          listings: newlyVisibleListings,
          itemListId: SRP_ITEM_LIST_ID,
          itemListName: SRP_ITEM_LIST_NAME,
          numberSearchResults: total,
          sortSelection: filters.sort,
          experimentFlagValue: featureFlags?.[EXPERIMENT_FLAG_KEY],
          numSearchResultsWithin: getPropertyTotalAmount(
            {
              total,
              nearbyTotal:
                (assets.nearbyTotal || 0) > (assets.offset || 0)
                  ? assets.nearbyTotal
                  : total - (assets.nearbyTotal || 0),
            },
            filters.globalSearch
          ),
          numSearchResultsNearby: nearbyAssetsList.length,
          searchResultsRadius: filters.nearbySearchRadius,
        })
      }
    },
    [
      itemsPerRow,
      assetsList.length,
      exactMatchList,
      nearbyAssetsList,
      total,
      filters,
      featureFlags,
      assets.nearbyTotal,
      assets.offset,
    ]
  )

  const scrollToRow = useCallback(
    targetListingId => {
      setHover(targetListingId, 'list')
      const targetRow = getTargetRow(
        targetListingId,
        exactMatchList,
        nearbyAssetsList,
        itemsPerRow
      )
      const headerRowOffset = 0
      isNumber(targetRow) &&
        listRef.current?.scrollToItem(targetRow + headerRowOffset, 'center')
    },
    [setHover, exactMatchList, nearbyAssetsList, itemsPerRow]
  )

  useHoverSubscription(({ id, source }) => {
    if (source === 'map') {
      scrollToRow(id)
    }
  })

  const prevFetchAssetsProgressRefScroll = useRef(fetchProgress)

  const scrollToSelectedPosition = useCallback(() => {
    const listingIdToScrollTo = getTargetListingToScroll()
    if (listingIdToScrollTo?.length) {
      setHover(listingIdToScrollTo, 'list')
      history.push(blueprintRemoveSelectedListingQueryParams())
    } else {
      scrollToTop()
    }
  }, [setHover, history])

  useEffect(() => {
    if (
      prevFetchAssetsProgressRefScroll.current?.loading === true &&
      fetchProgress?.loading === false
    ) {
      scrollToSelectedPosition()
    }
    prevFetchAssetsProgressRefScroll.current = fetchProgress
  }, [fetchProgress, scrollToSelectedPosition])

  const isAdglareCardDisplayed = shouldDisplayAdglareCard(
    exactMatchList,
    featureFlags
  )

  // need to be careful about where it's set or what our source is depending on markers
  if (isAdglareCardDisplayed) {
    const adglarePlaceholderListing = {
      injectedContentConfiguration: assets.adglareContent
        ? { htmlOverride: assets.adglareContent }
        : undefined,
      injectPlaceholder: true,
    }

    exactMatchList.splice(
      featureFlags['fe.resi.srp-adglare-configuration']?.position - 1,
      0,
      adglarePlaceholderListing
    )
  }

  const shouldShowLoadingPlaceholder =
    Boolean(!exactMatchList.length && !nearbyAssetsList.length) ||
    fetchProgress.loading

  const getTotalCountToDisplay = (
    total,
    assetsListLength,
    maxResultsAllowed
  ) => {
    const maxResultsAllowedDisplayed = maxResultsAllowed.toLocaleString('en-US')
    const totalResultsAvailable = total.toLocaleString('en-US')
    const assetListTotal = assetsListLength.toLocaleString('en-US')
    const resultsDisplayed =
      total > maxResultsAllowed ? (
        <>
          <span data-elm-id={`${BASE_ELM_ID}_max_results_allowed`}>
            {maxResultsAllowedDisplayed}
          </span>
          {' of '}
          <span data-elm-id={`${BASE_ELM_ID}_total_results_available`}>
            {totalResultsAvailable}
          </span>
        </>
      ) : (
        <>
          <span data-elm-id={`${BASE_ELM_ID}_total_results`}>
            {assetListTotal}
          </span>{' '}
        </>
      )

    return (
      <div className={cx(`${BASE_ELM_ID}-footer-totals`)}>
        Showing {resultsDisplayed}
      </div>
    )
  }

  const updateVerifiedImage = useCallback((listingId, value) => {
    verifiedImagesList.current[listingId] = value
  }, [])
  /**
   * Function to piece by piece construct the rows of the infinite list which comprises of the headers, sorting options, asset totals, actual listing cards to render, and the footer
   * @param {*} itemsPerRow the calculation of how many cards will fit in a row, requires the autosizer to determine the container size before we can use this to construct the actual list of rows
   * @param {*} rowCount the total assets to render divided by the itemsPerRow + the constant header/footer pieces
   * @param {*} cardProps card props sent to each card to allow it to function correctly
   * @returns
   */
  const getConstructedRows = (itemsPerRow, cardProps, width) => {
    const rows = []
    const isMobile = width < MOBILE_WIDTH_BREAKPOINT

    if (shouldShowLoadingPlaceholder) {
      rows.push({
        rowComponent: <LoadingState />,
        height: 300,
      })

      return rows
      // TODO: we return here because if loading placeholder is showing, then any rows added afterwards could cause visual bugs as the loading placeholder height can exceed row size,
      // this is fixable if we want to run the calculation based off of width with the loading placeholder card count of 12 at the time of this comment
    } else {
      // Add scrollable AssetTotals row on mobile when new navigation is enabled
      if (isNewNavigation && isMobile) {
        rows.push({
          rowComponent: (
            <div className={cx('scrollable-asset-totals')}>
              <AssetTotals
                assets={assets}
                elmId={`${BASE_ELM_ID}_totals_mobile`}
                filters={filters}
              />
            </div>
          ),
          height: 62,
        })
      }

      // construct card rows for exact match list
      for (
        let rowIndex = 0;
        rowIndex < Math.ceil(exactMatchList.length / itemsPerRow);
        rowIndex++
      ) {
        const fromIndex = rowIndex * itemsPerRow
        const toIndex = Math.min(fromIndex + itemsPerRow, exactMatchList.length)

        const rowItems = []
        for (let i = fromIndex; i < toIndex; i++) {
          const listingToRender = exactMatchList[i]
          rowItems.push(
            <CardItem
              key={
                listingToRender.listing_id ??
                (listingToRender.injectPlaceholder
                  ? `injected_placeholder_${i}`
                  : i)
              }
              listingToRender={listingToRender}
              cardProps={cardProps}
              resultSection='within_location'
              verifiedImage={
                verifiedImagesList.current[listingToRender.listing_id]
              }
              i={i}
              showAdContent={
                listingToRender?.injectPlaceholder ? true : undefined
              }
            />
          )
        }

        rows.push({
          rowComponent: (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {rowItems}
            </div>
          ),
          height: CARD_HEIGHT + GAP,
        })
      }

      // Add nearby match header row if applicable
      if (nearbyAssetsList.length > 0) {
        if (exactMatchList.length > 0) {
          rows.push({
            rowComponent: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <NearbyAssetTotals
                  nearbyTotal={
                    total -
                    exactMatchList.length +
                    (isAdglareCardDisplayed ? 1 : 0)
                  }
                  filters={filters}
                  elmId='away_header'
                />
              </div>
            ),
            height: width < MOBILE_WIDTH_BREAKPOINT ? 50 : 62,
          })
        }

        for (
          let rowIndex = 0;
          rowIndex < Math.ceil(nearbyAssetsList.length / itemsPerRow);
          rowIndex++
        ) {
          const fromIndex = rowIndex * itemsPerRow
          const toIndex = Math.min(
            fromIndex + itemsPerRow,
            nearbyAssetsList.length
          )

          const nearbyRowItems = []
          for (let i = fromIndex; i < toIndex; i++) {
            const listingToRender = nearbyAssetsList[i]
            nearbyRowItems.push(
              <CardItem
                key={'nearby_' + listingToRender.listing_id}
                listingToRender={listingToRender}
                cardProps={cardProps}
                resultSection='nearby'
                verifiedImage={
                  verifiedImagesList.current[listingToRender.listing_id]
                }
                i={i + (exactMatchList.length || 0)}
              />
            )
          }

          rows.push({
            rowComponent: (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {nearbyRowItems}
              </div>
            ),
            height: CARD_HEIGHT + GAP,
          })
        }
      }

      // Add footer row
      rows.push({
        rowComponent: (
          <>
            <BreadCrumbs filters={filters} />
            {getTotalCountToDisplay(
              total,
              assets.list.length,
              getMaxResultsAllowed(featureFlags)
            )}
            <div className={cx('footer-container')}>
              <hr className={cx('footer-separator')} />
              <SeoContent filters={filters} assets={assets} />
              <Footer />
            </div>
          </>
        ),
        height: 300,
      })
    }

    return rows
  }

  const onRowSizeUpdated = newSize => {
    if (newSize !== itemsPerRow) {
      setItemsPerRow(newSize)
    }
  }

  const onUpdateSelectedListingId = useCallback(
    targetListingId => {
      setHover(targetListingId, 'list')
    },
    [setHover]
  )

  const trackingContext = useMemo(
    () => ({
      itemListId: SRP_ITEM_LIST_ID,
      itemListName: SRP_ITEM_LIST_NAME,
      recommendationType: SRP_ITEM_LIST_ID,
      numberSearchResults: total,
      sortSelection: filters.sort,
      experimentFlagValue: featureFlags?.[EXPERIMENT_FLAG_KEY],
      numSearchResultsWithin: getPropertyTotalAmount(
        {
          total,
          nearbyTotal:
            (assets.nearbyTotal || 0) > (assets.offset || 0)
              ? assets.nearbyTotal
              : total - (assets.nearbyTotal || 0),
        },
        filters.globalSearch
      ),
      numSearchResultsNearby: nearbyAssetsList.length,
      searchResultsRadius: filters.nearbySearchRadius,
    }),
    [total, filters, featureFlags, assets, nearbyAssetsList]
  )

  // cardProps is stable so React.memo(CardItem) bails on list re-renders that
  // don't change a card's own data (e.g. saving a different property). Per-card
  // updates (hover, saved-state) come through the useIsHovered / useSavedState
  // subscriptions inside CardItem, not through the list re-rendering.
  const cardProps = useMemo(
    () => ({
      featureFlags,
      toggleSaveAsset,
      updateSelectedId: onUpdateSelectedListingId,
      updateOpenedAsset,
      updateVerifiedImage,
      trackingContext,
    }),
    [
      featureFlags,
      toggleSaveAsset,
      onUpdateSelectedListingId,
      updateOpenedAsset,
      updateVerifiedImage,
      trackingContext,
    ]
  )

  return (
    <div data-elm-id={BASE_ELM_ID} className={cx(`${BASE_ELM_ID}`)}>
      <div
        onMouseLeave={onMouseLeave}
        data-elm-id={`${BASE_ELM_ID}_content_wrapper`}
        className={cx(`asset-list-content-wrapper`)}
      >
        <div className={cx('asset-list-header')}>
          <div className={cx({ 'hide-on-mobile': isNewNavigation })}>
            <AssetTotals
              assets={assets}
              elmId={`${BASE_ELM_ID}_totals`}
              filters={filters}
            />
          </div>
          <div
            className={cx('header-second-row', {
              'hide-on-mobile': featureFlags['fe.resi.enable-new-navigation'],
            })}
          >
            <ListHeaders
              elmId={`${BASE_ELM_ID}_headers`}
              featureFlags={featureFlags}
              filters={filters}
              updateSorting={updateSorting}
            />
            <NearbyCheckboxRedesign
              assets={assets}
              elmId={BASE_ELM_ID}
              filters={filters}
              updateFiltersAction={updateFiltersAction}
            />
          </div>
          <div className={cx('list-divider')} />
        </div>

        <div
          data-elm-id={`${BASE_ELM_ID}_content_v2`}
          className={cx('property-card-container')}
        >
          <AutoSizer>
            {({ height, width }) => {
              const updatedItemsPerRow = calculateItemsPerRow(width)

              const rows = getConstructedRows(
                updatedItemsPerRow,
                cardProps,
                width
              )

              return (
                <DynamicSizeList
                  key={forceRemountKey}
                  listRef={listRef}
                  itemsPerRow={updatedItemsPerRow}
                  height={height}
                  width={width}
                  rows={rows}
                  onRowSizeUpdated={onRowSizeUpdated}
                  onItemsRendered={onItemsRendered}
                  elmId={BASE_ELM_ID}
                />
              )
            }}
          </AutoSizer>
        </div>
      </div>
    </div>
  )
}

ListingListComponentBase.propTypes = {
  assets: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  featureFlags: PropTypes.object.isRequired,
  toggleSaveAsset: PropTypes.func.isRequired,
  updateSorting: PropTypes.func.isRequired,
  updateFiltersAction: PropTypes.func.isRequired,
  updateOpenedAsset: PropTypes.func.isRequired,
}

// Memoized so unrelated searchPanel re-renders (hover, nudge/criteria state)
// don't rebuild the virtualized row model and thrash the scroll.
export const ListingListComponent = React.memo(ListingListComponentBase)
